import './logging'

import { createRoot } from 'react-dom/client'
import { RouterProvider } from 'react-router-dom'
import { ThemeProvider } from '@mui/material/styles'
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs'
import 'dayjs/locale/en-nz'
import 'dayjs/locale/en-au'
import { LocalizationProvider } from '@mui/x-date-pickers-pro/LocalizationProvider'
import { LicenseInfo } from '@mui/x-license-pro'
import { SnackbarProvider, SnackbarProviderProps } from 'notistack'

import theme from './theme'
import router from './router'
import { QueryClientProvider } from '@tanstack/react-query'
import { logger } from '$utils'
import { singletonQueryClient } from './utils/queryClient'
import LoadedSnackbar from './components/common/app/LoadedSnackbar'
import { Action } from './utils/snackbar'

import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import isBetween from 'dayjs/plugin/isBetween'
import relativeTime from 'dayjs/plugin/relativeTime'
import weekday from 'dayjs/plugin/weekday'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import updateLocale from 'dayjs/plugin/updateLocale'
import minMax from 'dayjs/plugin/minMax'
import isLeapYear from 'dayjs/plugin/isLeapYear'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'

dayjs.extend(weekday)
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)
dayjs.extend(isBetween)
dayjs.extend(relativeTime)
dayjs.extend(updateLocale)
dayjs.extend(advancedFormat)
dayjs.extend(minMax)
dayjs.extend(isLeapYear)

LicenseInfo.setLicenseKey(
  '9642a066f63647fa2f89072fc7b8d78dTz0xMDM3NDMsRT0xNzY1MDczNDMxMDAwLFM9cHJvLExNPXN1YnNjcmlwdGlvbixQVj1pbml0aWFsLEtWPTI='
)

const snackbarProviderProps: SnackbarProviderProps = {
  anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
  maxSnack: 3,
  autoHideDuration: 6000,
  action: Action,
  preventDuplicate: true,
  hideIconVariant: true,
  Components: {
    loadedSnackbar: LoadedSnackbar,
  },
}

logger.info('React app starting')

// We defer rendering of the app until the DOM is loaded
// this avoids a race condition where this code ends up
// running before the DOM element we try and render the
// app into is available.
//
// TODO - this deferring won't be required once we get rid of the AngularJS
// app 'host'. The #react-container div is currently being rendered by the
// AngularJS app so it isn't immediately availahle.
window.addEventListener('DOMContentLoaded', () => {
  const container: HTMLElement | null =
    document.getElementById('react-container')

  if (container) {
    logger.info('Rendering Loaded React app')
    const root = createRoot(container)
    root.render(
      <ThemeProvider theme={theme}>
        <SnackbarProvider {...snackbarProviderProps}>
          <QueryClientProvider client={singletonQueryClient}>
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale={'en-nz'}
            >
              <RouterProvider router={router} />
            </LocalizationProvider>
            <ReactQueryDevtools initialIsOpen={false} />
          </QueryClientProvider>
        </SnackbarProvider>
      </ThemeProvider>
    )
  }
})
